import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Search, Check, ChevronDown, X } from 'lucide-react';
import './MultiSelectDropdown.css';

const MultiSelectDropdown = ({ 
  options, 
  selectedValues, 
  onChange, 
  placeholder,
  label,
  icon: Icon
}) => {
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [menuStyle, setMenuStyle] = useState(null);
  const containerRef = useRef(null);
  const triggerRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (!isOpen) {
      setMenuStyle(null);
      return;
    }

    const updateMenuPosition = () => {
      const triggerEl = triggerRef.current;
      if (!triggerEl) return;

      const rect = triggerEl.getBoundingClientRect();
      setMenuStyle({
        position: 'fixed',
        top: rect.bottom + 4,
        left: rect.left,
        width: rect.width,
        zIndex: 2000,
      });
    };

    updateMenuPosition();

    window.addEventListener('resize', updateMenuPosition);
    window.addEventListener('scroll', updateMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateMenuPosition);
      window.removeEventListener('scroll', updateMenuPosition, true);
    };
  }, [isOpen]);

  const filteredOptions = options.filter(option => 
    option.label?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleToggleOption = (value) => {
    const newValues = selectedValues.includes(value)
      ? selectedValues.filter(v => v !== value)
      : [...selectedValues, value];
    onChange(newValues);
  };

  const handleSelectAll = () => {
    if (selectedValues.length === options.length) {
      onChange([]);
    } else {
      onChange(options.map(o => o.value));
    }
  };

  const selectedLabels = options
    .filter(o => selectedValues.includes(o.value))
    .map(o => o.label);

  const displayText = selectedLabels.length === 0 
    ? (placeholder || t('multiSelect.placeholder'))
    : selectedLabels.length === 1 
      ? selectedLabels[0]
      : t('multiSelect.selectedCount', { count: selectedLabels.length });

  return (
    <div className="multi-select-dropdown" ref={containerRef}>
      {label && (
        <label className="dropdown-label">
          {Icon && <Icon size={16} />}
          {label}
        </label>
      )}
      
      <div 
        className={`dropdown-trigger ${isOpen ? 'open' : ''}`}
        ref={triggerRef}
        onClick={() => setIsOpen(!isOpen)}
      >
        <span className={selectedValues.length === 0 ? 'placeholder' : ''}>
          {displayText}
        </span>
        <ChevronDown size={18} />
      </div>

      {isOpen && (
        <div className="dropdown-menu" style={menuStyle || undefined}>
          <div className="dropdown-search">
            <Search size={16} />
            <input
              type="text"
              placeholder={t('multiSelect.searchPlaceholder')}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
            {searchTerm && (
              <button className="clear-search" onClick={() => setSearchTerm('')}>
                <X size={14} />
              </button>
            )}
          </div>

          <div className="dropdown-actions">
            <button className="select-all-btn" onClick={handleSelectAll}>
              {selectedValues.length === options.length ? t('multiSelect.deselectAll') : t('multiSelect.selectAll')}
            </button>
          </div>

          <div className="dropdown-options">
            {filteredOptions.length === 0 ? (
              <div className="no-results">{t('multiSelect.noResults')}</div>
            ) : (
              filteredOptions.map(option => {
                const isSelected = selectedValues.includes(option.value);
                return (
                  <div
                    key={option.value}
                    className={`dropdown-option ${isSelected ? 'selected' : ''}`}
                    onClick={() => handleToggleOption(option.value)}
                  >
                    <div className="option-checkbox">
                      {isSelected && <Check size={14} />}
                    </div>
                    <span className="option-label">{option.label}</span>
                  </div>
                );
              })
            )}
          </div>

          {selectedValues.length > 0 && (
            <div className="dropdown-footer">
              {t('multiSelect.selectedCount', { count: selectedValues.length })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MultiSelectDropdown;
